import { useCallback, useEffect, useState } from "react";
import {
  acceptResourceAllocationSettlement,
  getResourceAllocationSummary,
  rejectResourceAllocationSettlement,
} from "../services/resourceAllocation.service";
import { monthNames } from "../utils/consts";
import type { IResourceAllocationSummaryRowApi } from "../services/types/resourceAllocation";

export default function useResourceAllocationSummary(
  selectedMonth: string,
  selectedYear: number
) {
  const [rows, setRows] = useState<IResourceAllocationSummaryRowApi[]>([]);
  const [year, setYear] = useState(selectedYear);
  const [month, setMonth] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const monthIndex = monthNames.indexOf(selectedMonth);

  const fetchData = useCallback(async () => {
    if (monthIndex < 0) return;

    setRows([]);
    setIsLoading(true);
    try {
      const data = await getResourceAllocationSummary(selectedYear, monthIndex + 1);
      setRows(data.rows);
      setYear(data.year);
      setMonth(data.month);
    } catch (err) {
      console.error("❌ Błąd pobierania alokacji zasobów:", err);
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, [monthIndex, selectedYear]);

  const acceptSettlement = async (row: IResourceAllocationSummaryRowApi) => {
    if (monthIndex < 0) return;

    await acceptResourceAllocationSettlement(
      row.employeeId,
      selectedYear,
      monthIndex + 1
    );

    await fetchData();
  };

  const rejectSettlement = async (
    row: IResourceAllocationSummaryRowApi,
    rejectionReason: string
  ) => {
    if (monthIndex < 0) return;

    await rejectResourceAllocationSettlement(
      row.employeeId,
      selectedYear,
      monthIndex + 1,
      rejectionReason
    );

    await fetchData();
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    rows,
    year,
    month,
    isLoading,
    refetch: fetchData,
    acceptSettlement,
    rejectSettlement,
    monthIndex,
  };
}
